import React, { useEffect, useState } from "react";
// Media Query
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useDispatch, useSelector } from "react-redux";
import { getMedicines } from "../reduxToolkit/features/adminPanel/medicine";
import LoadingBox from "../components/misc/Loading";
import {
  Autocomplete,
  Box,
  Card,
  CardActions,
  CardContent,
  createFilterOptions,
  Grid,
  Pagination,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BreadcrumbsTemplate from "../components/misc/BreadcrumbsTemplate";
import { PF } from "../config";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { makeUrlFriendly } from "../hooks/makeUrlFriendly";
import { mergeMedicineList } from "../hooks/mergeMedicineList";
import { Link } from "react-router-dom";
import { useLangPath } from "../hooks/langAlias";
import { colors, eyebrowSx, fonts, radii, shadows } from "../theme/designTokens";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Limit options to show in autocomplete
const OPTIONS_LIMIT = 12;
const defaultFilterOptions = createFilterOptions();

const filterOptions = (options, state) => {
  return defaultFilterOptions(options, state).slice(0, OPTIONS_LIMIT);
};

function Generics() {
  // Responsive
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  // UseState
  const [genericLoading, setGenericLoading] = useState(false);
  const [searchedGenericLoading, setSearchedGenericLoading] = useState(false);
  const [genericList, setGenericList] = useState([]);
  const [searchedGenericList, setSearchedGenericList] = useState([]);

  const [pageNum, setPageNum] = useState(1);
  const [productViewStart, setProductViewStart] = useState(0);
  const [totalPage, setTotalPage] = useState(0);

  const [lang, setLang] = useState("en");

  // store
  const { medicines } = useSelector((state) => state.medicine);
  const { activeLangFront } = useSelector((state) => state.miscFront);
  const lp = useLangPath();

  // Functions
  const getDefaultImage = (x) => {
    if (x.image && x.image.length) {
      const I = x.image.find((i) => i.default);
      if (I) return I;
    }
    return { src: "" };
  };

  const getGenericBrand = (id) => {
    let genericBrand = "";
    const M = medicines.find((m) => m.lang === "en");
    if (M) {
      const med = M.data.find((x) => x.fixedMedicineId === id);
      if (med) genericBrand = med.genericBrand;
    }
    return genericBrand;
  };

  const handleSearchClick = (e, newValue) => {
    setSearchedGenericLoading(true);
    console.log(newValue);
    let arr = genericList.filter((generic) =>
      newValue.find((value) => value.genericName === generic.genericName)
    );
    setSearchedGenericList(arr);
    setTimeout(() => {
      setSearchedGenericLoading(false);
    }, 100);
  };

  const handlePageChange = (event, value) => {
    setPageNum(value);
    setTimeout(() => {
      scroll();
    }, 250);
  };

  const scroll = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Use Effect
  const dispatch = useDispatch();
  useEffect(() => {
    // if (!(medicines && medicines.length > 0))
    dispatch(getMedicines());
    setTimeout(() => {
      scroll();
    }, 250);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set Language
  useEffect(() => {
    if (activeLangFront) setLang(activeLangFront.langCode);
  }, [activeLangFront]);

  // Set Generic List
  useEffect(() => {
    setGenericLoading(true);
    let arr = [];
    if (lang !== "" && medicines && medicines.length > 0) {
      const mergedData = mergeMedicineList(medicines, lang);
      if (mergedData.length > 0) {
        let arr1 = [];
        mergedData.forEach((x) => {
          let obj = { ...x };
          arr1.push(obj);
        });

        arr = [...arr1].sort((a, b) =>
          a.generic.toLowerCase().replace(/\s/g, "") >
          b.generic.toLowerCase().replace(/\s/g, "")
            ? 1
            : -1
        );
      } else {
        setTotalPage(0);
      }
      setTimeout(() => {
        setGenericLoading(false);
      }, 2000);
    }
    arr = arr.filter((m) => m.visible === true);
    let tempArr = [...arr];
    let unique = [
      ...new Map(tempArr.map((item) => [item["generic"], item])).values(),
    ];
    let genericArr = [];
    unique.forEach((item) => {
      let genericObj = {
        genericName: item.generic,
        medicines: arr.filter((m) => m.generic === item.generic),
      };
      genericArr.push(genericObj);
    });
    setTotalPage(Math.ceil(genericArr.length / 24));

    setGenericList(genericArr);
  }, [medicines, lang]);

  useEffect(() => {
    if (pageNum > 1) setProductViewStart((pageNum - 1) * 24);
    else setProductViewStart(0);
  }, [pageNum]);

  // Set Total Page
  useEffect(() => {
    if (genericList.length > 0)
      setTotalPage(Math.ceil(genericList.length / 24));
  }, [genericList.length]);

  //! Translate
  // Store
  const { langArrFromClientSideFrontend } = useSelector(
    (state) => state.miscFront
  );
  const translateFront = (String) => {
    let translation = String;
    if (
      langArrFromClientSideFrontend &&
      langArrFromClientSideFrontend.length > 0
    ) {
      const T = langArrFromClientSideFrontend.find((l) => l.title === String);
      if (T && T.value) {
        let obj = { ...T };
        if (T.value === "") obj.value = T.title;
        translation = T.value;
      }
    }
    return translation;
  };
  //! Translate

  return (
    <Box sx={{ margin: "auto" }}>
      <BreadcrumbsTemplate b3={translateFront("Generics")} />

      {genericLoading ? (
        <LoadingBox w={"100%"} h={700} />
      ) : (
        <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, sm: 3, md: 5 }, pb: 6 }}>
          <Box sx={{ textAlign: "center", py: { xs: 3, md: 4 } }}>
            <Box sx={{ ...eyebrowSx, mx: "auto", width: "fit-content" }}>
              Browse By Generic
            </Box>
            <Typography
              sx={{
                fontFamily: fonts.display,
                fontWeight: 800,
                fontSize: { xs: "1.7rem", md: "2.1rem" },
                color: colors.ink,
                mt: 1.5,
              }}
            >
              {translateFront("Generics")}
            </Typography>
          </Box>

          <Autocomplete
            disablePortal
            multiple
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: radii.md,
                bgcolor: colors.paperTint,
                "& fieldset": { border: "none" },
                "&.Mui-focused fieldset": { border: `1px solid ${colors.primary}` },
              },
            }}
            filterOptions={filterOptions}
            options={genericList}
            getOptionLabel={(option) => `${option.genericName}`}
            onChange={(e, newValue) => handleSearchClick(e, newValue)}
            onKeyPress={(e, newValue) =>
              e.key === "Enter" && handleSearchClick(e, newValue)
            }
            renderOption={(props, option) => (
              <Box
                component="li"
                sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                {...props}
              >
                {option.genericName}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translateFront("Search Generics...")}
                variant="outlined"
                size="small"
              />
            )}
          />
          {searchedGenericLoading ? (
            <LoadingBox w={"100%"} h={700} />
          ) : (
            <Grid
              container
              justifyContent="center"
              alignItems="stretch"
              spacing={3}
            >
              {(searchedGenericList.length
                ? searchedGenericList
                : genericList.slice(productViewStart, productViewStart + 24)
              ).map((generic, index) => (
                <Grid item key={index} xs={12} sm={6} md={6} lg={3}>
                  <Card
                    sx={{
                      minWidth: 275,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: radii.lg,
                      boxShadow: shadows.card,
                      border: `1px solid ${colors.line}`,
                      overflow: "hidden",
                      transition: "box-shadow .15s ease, transform .15s ease",
                      "&:hover": { boxShadow: shadows.cardHover, transform: "translateY(-2px)" },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: fonts.display,
                          fontWeight: 700,
                          fontSize: "1rem",
                          color: colors.ink,
                          mb: 1,
                        }}
                      >
                        {generic.genericName.toUpperCase()}
                      </Typography>
                      <Grid container spacing={1}>
                        {generic.medicines
                          .slice(0, generic.medicines.length > 3 ? 2 : 3)
                          .map((medicine, index) => (
                            <Grid item key={index} xs={4}>
                              <Link
                                to={lp(
                                  `/our-medicines/${makeUrlFriendly(
                                    getGenericBrand(
                                      lang !== "en"
                                        ? medicine.medicineDetectorId
                                        : medicine.fixedMedicineId
                                    )
                                  )}`
                                )}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    minHeight: 100,
                                    bgcolor: colors.white,
                                    borderRadius: radii.md,
                                    border: `1px solid ${colors.line}`,
                                  }}
                                >
                                  <LazyLoadImage
                                    src={
                                      getDefaultImage(medicine) &&
                                      getDefaultImage(medicine).src !== ""
                                        ? PF +
                                          "adminPanel/medicines/img/" +
                                          getDefaultImage(medicine).src
                                        : PF +
                                          "adminPanel/medicines/no-medicine-image.jpg"
                                    }
                                    alt={" medicine " + medicine.generic}
                                    effect="blur"
                                    placeholderSrc={PF + "adminPanel/medicines/no-medicine-image.jpg"}
                                    style={{
                                      objectFit: "contain",
                                      height: !smUp ? 70 : 90,
                                    }}
                                  />
                                </Box>
                              </Link>
                              <Typography
                                align="center"
                                sx={{ fontSize: 11.5, color: colors.ink2, mt: 0.5 }}
                              >
                                {medicine.genericBrand}
                              </Typography>
                            </Grid>
                          ))}
                        {generic.medicines.length > 3 && (
                          <Grid
                            item
                            xs={4}
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              fontSize: 12.5,
                              fontWeight: 700,
                              color: colors.primary,
                            }}
                          >
                            + {generic.medicines.length - 3}{" "}
                            {translateFront("More")}
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                    <CardActions
                      sx={{
                        bgcolor: colors.paperTint,
                        borderTop: `1px solid ${colors.line}`,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Link
                        to={lp(
                          `/generics/${makeUrlFriendly(generic.genericName)}`
                        )}
                        style={{ textDecoration: "none" }}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 13,
                            fontWeight: 700,
                            color: colors.primary,
                            "&:hover": { color: colors.primaryDark },
                          }}
                        >
                          {translateFront("View Generic")}{" "}
                          <ArrowForwardIcon sx={{ fontSize: 15 }} />
                        </Box>
                      </Link>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          {!searchedGenericList.length &&
            totalPage >= 1 &&
            genericList.length > 0 && (
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  mt: 2,
                }}
              >
                <Stack spacing={2}>
                  <Pagination
                    count={totalPage}
                    color="primary"
                    onChange={handlePageChange}
                    size={smUp ? "medium" : "small"}
                    hideNextButton={smUp ? false : true}
                    hidePrevButton={smUp ? false : true}
                  />
                </Stack>
              </Grid>
            )}
        </Box>
      )}
    </Box>
  );
}

export default Generics;
